import { Box, Card, CardContent, Typography } from '@mui/material';
import { displayBoolean } from '../../../common/helpers/displayBoolean';
import { FnaBuyAndSellNeeds, FNADetails } from '../../../common/types/analyses';
import { formatAmount } from '../../../common/components/dataGrid/dataGridEditNumberCell';
import { JSX } from 'react';
import { PolicyCardSectionProps } from '../../../common/components/policyCard/policyCardSection';
import PolicyCard from '../../../common/components/policyCard';

interface TraditionalLayoutProps {
  fna: FNADetails;
  buyAndSellNeeds: FnaBuyAndSellNeeds | undefined;
}

/**
 * Display the FNA buy and sell needs results (traditional layout)
 */
const TraditionalLayout = ({ buyAndSellNeeds, fna }: TraditionalLayoutProps): JSX.Element => (
  <Box className="mt-12 grid lg:grid-cols-2 gap-12">
    {buyAndSellNeeds?.needs.map((need) => (
      <Card key={need.id}>
        <CardContent>
          <PolicyCard
            className="overflow-hidden"
            sections={[
              {
                topBorder: false,
                caption: 'Insured life',
                title: need.representativeRoleplayerName || need.partyRoleplayerName,
                inlineItems: true,
                items: [
                  need.representativeRoleplayerName && {
                    name: 'Representative life for',
                    value: need.partyRoleplayerName,
                  },
                  {
                    name: 'Tax status',
                    value:
                      need.representativeRoleplayerTaxStatusNote ||
                      need.partyRoleplayerTaxStatusNote,
                  },
                  {
                    name: 'ID number',
                    value: need.representativeRoleplayerIDReg || need.partyRoleplayerIDReg,
                  },
                  {
                    name: 'Marriage type',
                    value: need.marriageTypeNote,
                  },
                  { name: 'Capacity', value: need.capacityNote },
                ].filter(Boolean) as PolicyCardSectionProps['items'],
              },
              {
                caption: 'Needs',
                inlineItems: true,
                items: [
                  {
                    name: 'Total loan accounts',
                    value: formatAmount(need.loanaccount),
                  },
                  {
                    name: 'Purchase price of interest',
                    value: formatAmount(need.purchasePrice),
                  },
                  {
                    name: 'Existing provision',
                    value: formatAmount(need.existingprovision),
                  },
                  need.estateDuty > 0 && {
                    name: 'Provide for estate duty',
                    value: `${formatAmount(need.estateDuty)} (${need.estateDutyRate}%)`,
                  },
                  {
                    name: 'Disability benefit',
                    value: `${displayBoolean(need.disabilityBenefit)} ${
                      need.disabilityBenefit && need.disabilityBenefitAmount
                        ? `(${formatAmount(need.disabilityBenefitAmount)})`
                        : ''
                    }`,
                  },
                ].filter(Boolean) as PolicyCardSectionProps['items'],
              },
              {
                caption: 'Death benefit',
                inlineItems: true,
                items: [
                  { value: formatAmount(need.deathBenefit), large: true },
                  {
                    name: 'Net existing provision',
                    value: formatAmount(need.netExistingProvision),
                    className: '-mt-5',
                  },
                ],
              },
              {
                inlineItems: true,
                items: [
                  {
                    name: 'Business valuation',
                    value: formatAmount(fna.buyandsell?.valuationValue),
                  },
                  {
                    name: 'Valuation method',
                    value: `${fna.buyandsell?.valuationMethodNote} | ${fna.buyandsell?.valuationBasisNote}`,
                  },
                ],
              },
              {
                caption: 'Policy owners',
                itemsDivider: false,
                items: need.policyHolders?.map((item) => ({
                  value: (
                    <Box className="space-x-4 divide-x flex">
                      <Typography fontWeight={700}>{item.partyRoleplayerName}</Typography>
                      <Box className="flex space-x-2">
                        <Typography className="pl-4">Policy ownership: </Typography>
                        <Typography fontWeight={700}>{item.ownership}%</Typography>
                      </Box>
                    </Box>
                  ),
                })),
              },
            ]}
          />
        </CardContent>
      </Card>
    ))}
  </Box>
);

export default TraditionalLayout;
